import { readFile } from 'node:fs/promises';
import { join } from 'node:path';
import { createHash } from 'node:crypto';
import { execa } from 'execa';
import { engineLog } from '@/core/logger-registry.js';
import type { NarrowedScope } from './file-check-mapper.js';
import { FileCheckMapper } from './file-check-mapper.js';

export interface IncrementalScanOptions {
  projectRoot: string;
  lastRunId: string;
  frameworks: string[];
  fullScanStalenessThresholdDays?: number;
}

export interface IncrementalScanResult {
  no_security_changes: boolean;
  narrowed_scope: NarrowedScope;
  changed_files: string[];
  last_run_id: string;
}

export class IncrementalScanner {
  constructor(private readonly mapper: FileCheckMapper) {}

  async scan(options: IncrementalScanOptions): Promise<IncrementalScanResult> {
    const changedFiles = await this.detectChangedFiles(options.projectRoot, options.lastRunId);

    const narrowedScope = this.mapper.map(changedFiles);

    await this.warnIfFullScanStale(
      options.projectRoot,
      options.fullScanStalenessThresholdDays ?? 14,
    );

    return {
      no_security_changes: narrowedScope.no_security_changes,
      narrowed_scope: narrowedScope,
      changed_files: changedFiles,
      last_run_id: options.lastRunId,
    };
  }

  private async detectChangedFiles(projectRoot: string, lastRunId: string): Promise<string[]> {
    // Try git diff first
    try {
      const gitFiles = await this.gitDiff(projectRoot, lastRunId);
      if (gitFiles.length > 0) {
        return gitFiles;
      }

      const hashFiles = await this.hashDiff(projectRoot, lastRunId);
      return hashFiles.length > 0 ? hashFiles : gitFiles;
    } catch {
      // fall through to hash-based diff
    }

    // Fall back to hash-based comparison
    try {
      return await this.hashDiff(projectRoot, lastRunId);
    } catch {
      return [];
    }
  }

  private async gitDiff(projectRoot: string, lastRunId: string): Promise<string[]> {
    // Try to get the git commit from the last run progress
    const progressPath = join(projectRoot, '.paqad', 'pentest', 'runs', lastRunId, 'progress.json');

    let baseCommit: string | undefined;
    try {
      const raw = await readFile(progressPath, 'utf8');
      const progress = JSON.parse(raw) as { git_commit?: string };
      baseCommit = progress.git_commit;
    } catch {
      // no progress file
    }

    if (!baseCommit) {
      // Use HEAD~1 as fallback
      baseCommit = 'HEAD~1';
    }

    const result = await execa('git', ['diff', '--name-only', baseCommit], {
      cwd: projectRoot,
    });

    return result.stdout
      .split('\n')
      .map((f) => f.trim())
      .filter(Boolean);
  }

  private async hashDiff(projectRoot: string, lastRunId: string): Promise<string[]> {
    const manifestPath = join(projectRoot, '.paqad', 'pentest', 'runs', lastRunId, 'progress.json');

    let fileManifest: Record<string, string>;
    try {
      const raw = await readFile(manifestPath, 'utf8');
      const progress = JSON.parse(raw) as { file_hashes?: Record<string, string> };
      fileManifest = progress.file_hashes ?? {};
    } catch {
      return [];
    }

    const changed: string[] = [];
    for (const [filePath, storedHash] of Object.entries(fileManifest)) {
      try {
        const content = await readFile(join(projectRoot, filePath), 'utf8');
        const currentHash = createHash('sha256').update(content).digest('hex');
        if (currentHash !== storedHash) {
          changed.push(filePath);
        }
      } catch {
        changed.push(filePath); // file deleted
      }
    }

    return changed;
  }

  private async warnIfFullScanStale(projectRoot: string, thresholdDays: number): Promise<void> {
    try {
      const runsDir = join(projectRoot, '.paqad', 'pentest', 'runs');
      const { readdir } = await import('node:fs/promises');
      const runs = await readdir(runsDir).catch(() => []);

      // Find the most recent full scan
      for (const run of runs.sort().reverse()) {
        const progressPath = join(runsDir, run, 'progress.json');
        try {
          const raw = await readFile(progressPath, 'utf8');
          const progress = JSON.parse(raw) as { incremental_type?: string; started_at?: string };

          if (!progress.incremental_type || progress.incremental_type === 'full') {
            const startedAt = new Date(progress.started_at ?? 0);
            const ageDays = (Date.now() - startedAt.getTime()) / (1000 * 60 * 60 * 24);

            if (ageDays > thresholdDays) {
              engineLog(
                'warn',
                `[pentest] Warning: Last full pentest was ${Math.floor(ageDays)} days ago. ` +
                  `Consider running a full pentest (threshold: ${thresholdDays} days).`,
              );
            }
            return;
          }
        } catch {
          // skip
        }
      }
    } catch {
      // non-critical
    }
  }
}
